import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import PointInput from './components/PointInput';
import DrawingCanvas from './components/DrawingCanvas';
import ContourControls from './components/ContourControls';
import PointsList from './components/PointsList';
import { 
  convertCoordinates, 
  detectCoordinateSystem, 
  getUTMZone, 
  getProjectionString 
} from './utils/projections';
import { triangulatePoints, generateContourLines } from './utils/triangulation';
import { generateDXF, downloadDXF } from './utils/dxfExporter';

const App = () => {
  const [points, setPoints] = useState([]);
  const [displayPoints, setDisplayPoints] = useState([]);
  const [coordinateSystem, setCoordinateSystem] = useState('geographic');
  const [triangles, setTriangles] = useState([]);
  const [contourLines, setContourLines] = useState([]);
  const [showPointLabels, setShowPointLabels] = useState(true);
  const [showTriangulation, setShowTriangulation] = useState(false);
  const [showContours, setShowContours] = useState(true);

  const updateDisplayPoints = useCallback((newPoints, system) => {
    if (newPoints.length === 0) {
      setDisplayPoints([]);
      return;
    }

    let converted = newPoints;
    
    // Convertir a UTM para visualización si es necesario
    if (system === 'geographic') {
      const avgLon = newPoints.reduce((sum, p) => sum + p.x, 0) / newPoints.length;
      const utmZone = getUTMZone(avgLon);
      const targetProjection = getProjectionString(utmZone);
      converted = convertCoordinates(newPoints, 'EPSG:4326', targetProjection);
    }
    
    setDisplayPoints(converted);
  }, []);

  const handleAddPoint = (point) => {
    const newPoints = [...points, point];
    setPoints(newPoints);
    updateDisplayPoints(newPoints, coordinateSystem);
  };

  const handleImportPoints = (importedPoints) => {
    const detectedSystem = detectCoordinateSystem(importedPoints);
    setCoordinateSystem(detectedSystem === 'geographic' ? 'geographic' : 'utm30');
    setPoints(importedPoints);
    updateDisplayPoints(importedPoints, detectedSystem === 'geographic' ? 'geographic' : 'utm30');
  };

  const handleExportPoints = () => {
    const csvContent = points.map(p => `${p.id},${p.x},${p.y},${p.z}`).join('\n');
    const blob = new Blob([`ID,X,Y,Z\n${csvContent}`], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'puntos_topograficos.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleClearPoints = () => {
    setPoints([]);
    setDisplayPoints([]);
    setTriangles([]);
    setContourLines([]);
  };

  const handleDeletePoint = (pointId) => {
    const newPoints = points.filter(p => p.id !== pointId);
    setPoints(newPoints);
    updateDisplayPoints(newPoints, coordinateSystem);
    
    // Regenerar triangulación si había puntos
    if (newPoints.length >= 3) {
      const { triangles: newTriangles } = triangulatePoints(displayPoints.filter(p => p.id !== pointId));
      setTriangles(newTriangles);
    } else {
      setTriangles([]);
      setContourLines([]);
    }
  };

  const handleCoordinateSystemChange = (newSystem) => {
    if (points.length === 0) {
      setCoordinateSystem(newSystem);
      return;
    }

    let convertedPoints = points;
    
    // Convertir entre sistemas
    if (coordinateSystem === 'geographic' && newSystem.startsWith('utm')) {
      const zone = parseInt(newSystem.replace('utm', ''));
      const targetProjection = getProjectionString(zone);
      convertedPoints = convertCoordinates(points, 'EPSG:4326', targetProjection);
    } else if (coordinateSystem.startsWith('utm') && newSystem === 'geographic') {
      const zone = parseInt(coordinateSystem.replace('utm', ''));
      const sourceProjection = getProjectionString(zone);
      convertedPoints = convertCoordinates(points, sourceProjection, 'EPSG:4326');
    } else if (coordinateSystem.startsWith('utm') && newSystem.startsWith('utm')) {
      const sourceZone = parseInt(coordinateSystem.replace('utm', ''));
      const targetZone = parseInt(newSystem.replace('utm', ''));
      if (sourceZone !== targetZone) {
        const sourceProjection = getProjectionString(sourceZone);
        const targetProjection = getProjectionString(targetZone);
        convertedPoints = convertCoordinates(points, sourceProjection, targetProjection);
      }
    }

    setPoints(convertedPoints);
    setCoordinateSystem(newSystem);
    updateDisplayPoints(convertedPoints, newSystem);
  };

  const handleGenerateContours = async (interval) => {
    if (displayPoints.length < 3) return;

    const { triangles: newTriangles } = triangulatePoints(displayPoints);
    setTriangles(newTriangles);

    const contours = generateContourLines(newTriangles, displayPoints, interval);
    setContourLines(contours);
  };

  const handleExportDXF = () => {
    const dxfContent = generateDXF(displayPoints, contourLines, triangles);
    downloadDXF(dxfContent, 'geomapper_topografico.dxf');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="space-y-8"
        >
          {/* Header */}
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <a href="https://www.topodron3d.com" target="_blank" rel="noopener noreferrer" className="inline-block">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4 hover:opacity-80 transition-opacity">
                Curvado 3D
              </h1>
            </a>
            <p className="text-gray-600 text-lg">
              Procesamiento avanzado de nubes de puntos 3D con triangulación y curvas de nivel
            </p>
            <div className="mt-6 p-4 bg-white/80 rounded-xl border border-gray-200/50 shadow-sm">
              <p className="text-gray-500 text-sm">
                Espacio para tu logo y más información de tu web.
              </p>
              <a href="https://www.topodron3d.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline text-sm">
                www.topodron3d.com
              </a>
            </div>
          </motion.div>

          {/* Input de puntos */}
          <PointInput
            onAddPoint={handleAddPoint}
            onImportPoints={handleImportPoints}
            onExportPoints={handleExportPoints}
            onClearPoints={handleClearPoints}
            coordinateSystem={coordinateSystem}
            onCoordinateSystemChange={handleCoordinateSystemChange}
          />

          {/* Controles de curvas */}
          <ContourControls
            onGenerateContours={handleGenerateContours}
            onToggleTriangulation={() => setShowTriangulation(!showTriangulation)}
            onToggleContours={() => setShowContours(!showContours)}
            onTogglePointLabels={() => setShowPointLabels(!showPointLabels)}
            onExportDXF={handleExportDXF}
            showTriangulation={showTriangulation}
            showContours={showContours}
            showPointLabels={showPointLabels}
            hasPoints={points.length > 0}
            hasContours={contourLines.length > 0}
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Canvas de dibujo */}
            <div className="lg:col-span-2">
              <DrawingCanvas
                points={displayPoints}
                contourLines={contourLines}
                triangles={triangles}
                showPointLabels={showPointLabels}
                showTriangulation={showTriangulation}
                showContours={showContours}
              />
            </div>

            {/* Lista de puntos */}
            <div className="lg:col-span-1">
              <PointsList
                points={points}
                onDeletePoint={handleDeletePoint}
                coordinateSystem={coordinateSystem}
              />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <footer className="py-6 text-center text-gray-600 text-sm border-t border-gray-200/50 mt-12">
        Creado por{' '}
        <a href="https://www.topodron3d.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
          Topodron3d
        </a>
      </footer>
    </div>
  );
};

export default App;